import React from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import {
  FileText,
  BookOpen,
  Video,
  Download,
  ExternalLink,
  Lightbulb,
  TrendingUp,
  ArrowRight,
  Sparkles,
} from "lucide-react";

import {
  TechLabel,
  StatusBadge,
  CornerMarkers,
  PageContainer,
  PrimaryButton,
  motionFadeUp,
  C,
} from "../../design-system";
import { createPageUrl } from "../../utils";

const resources = [
  {
    category: "Product documentation",
    icon: FileText,
    accent: C.coral,
    items: [
      { title: "Platform overview",       desc: "Complete guide to the WebIdeal ecosystem", type: "PDF" },
      { title: "Product sketches",        desc: "Visual mockups of all four modules",       type: "Figma" },
      { title: "Technical architecture",  desc: "System design, infra, security model",     type: "PDF" },
    ],
  },
  {
    category: "Pitch & investor materials",
    icon: TrendingUp,
    accent: C.mint,
    items: [
      { title: "Investor pitch deck",     desc: "Full presentation (NDA on request)",       type: "PDF" },
      { title: "Market analysis report",  desc: "HRTech market insights and opportunity",   type: "PDF" },
      { title: "Financial projections",   desc: "5-year growth and revenue model",          type: "Excel" },
    ],
  },
  {
    category: "Case studies",
    icon: Lightbulb,
    accent: C.gold,
    items: [
      { title: "Enterprise transformation", desc: "How a 2,000-person org cut TTH by 60%",  type: "Story" },
      { title: "SMB success story",         desc: "Affordable AI hiring for early-stage",   type: "Story" },
      { title: "Agency use case",           desc: "Staffing agency scales 4× with WebIdeal", type: "Story" },
    ],
  },
  {
    category: "Videos & demos",
    icon: Video,
    accent: C.coral,
    items: [
      { title: "Product demo (5 min)",      desc: "Tour every module in five minutes",      type: "Video" },
      { title: "AI features showcase",      desc: "See the agents at work, end to end",     type: "Video" },
      { title: "Customer testimonials",     desc: "Hear from real users in the wild",       type: "Video" },
    ],
  },
];

const teasers = [
  { title: "The future of AI in recruitment",   excerpt: "Predictive analytics is reshaping every touchpoint.",     date: "Jan 15, 2025", readTime: "5 min" },
  { title: "Why unified HR platforms win",       excerpt: "The case for one record across ATS, CRM and HRMS.",      date: "Jan 10, 2025", readTime: "7 min" },
  { title: "Reducing bias with AI screening",    excerpt: "Data-driven hiring for more accountable teams.",         date: "Jan 05, 2025", readTime: "6 min" },
];

export default function Resources() {
  return (
    <div
      className="relative"
      style={{
        background: C.paper,
        color: C.forest,
        fontFamily: "'Plus Jakarta Sans', system-ui, -apple-system, sans-serif",
      }}
    >
      {/* HERO */}
      <section className="relative pt-16 pb-16 md:pt-24 md:pb-20 border-b ds-bg-spotlight" style={{ borderColor: C.hairline }}>
        <PageContainer>
          <motion.div initial="hidden" animate="show" variants={motionFadeUp}>
            <motion.div variants={motionFadeUp} custom={0}>
              <StatusBadge>Resources · 12 documents</StatusBadge>
            </motion.div>
            <motion.h1
              variants={motionFadeUp}
              custom={1}
              className="mt-5 text-5xl sm:text-6xl lg:text-[5rem] xl:text-[5.75rem] font-extrabold leading-[0.92] tracking-tight max-w-4xl"
              style={{ color: C.forest }}
            >
              Everything in
              <br />
              <span style={{ color: `${C.forest}80` }}>one library.</span>
            </motion.h1>
            <motion.p
              variants={motionFadeUp}
              custom={2}
              className="mt-6 text-lg md:text-xl leading-relaxed max-w-2xl"
              style={{ color: C.grid }}
            >
              Product docs, investor materials, customer case studies, and
              demo videos. Bookmark the page or grab the PDF your call.
            </motion.p>
          </motion.div>
        </PageContainer>
      </section>

      {/* RESOURCE CATEGORIES */}
      <section className="py-20 md:py-24 border-b" style={{ borderColor: C.hairline }}>
        <PageContainer>
          <div className="space-y-16">
            {resources.map((group, gi) => (
              <motion.div
                key={group.idx}
                initial={{ opacity: 0, y: 18 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, amount: 0.15 }}
                transition={{ duration: 0.45, delay: gi * 0.05 }}
              >
                <div className="mb-8 flex items-end justify-between flex-wrap gap-4">
                  <div className="flex items-center gap-4">
                    <div className="w-12 h-12 flex items-center justify-center" style={{ background: C.forest, color: C.paper }}>
                      <group.icon className="w-5 h-5" strokeWidth={1.6} />
                    </div>
                    <div>
                      <TechLabel style={{ color: group.accent }}>{group.idx}</TechLabel>
                      <h2 className="text-2xl md:text-3xl font-bold leading-tight" style={{ color: C.forest }}>
                        {group.category}
                      </h2>
                    </div>
                  </div>
                  <TechLabel style={{ color: `${C.grid}99` }}>{group.items.length} items</TechLabel>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-3 gap-px" style={{ background: C.hairline }}>
                  {group.items.map((item, i) => (
                    <button
                      key={item.title}
                      type="button"
                      className="ds-spatial relative p-7 md:p-8 group cursor-pointer transition-colors duration-200 hover:bg-white text-left w-full"
                      style={{ background: C.paper, borderLeft: `4px solid ${group.accent}` }}
                    >
                      <div className="flex items-start justify-between mb-5">
                        <span
                          className="inline-flex items-center px-2 py-1 text-[10px] font-bold uppercase border"
                          style={{
                            letterSpacing: "0.18em",
                            borderColor: `${group.accent}55`,
                            color: group.accent,
                          }}
                        >
                          {item.type}
                        </span>
                        <span
                          className="w-8 h-8 flex items-center justify-center border transition-colors duration-200 group-hover:bg-[#1A3C2B] group-hover:text-[#F7F7F5] group-hover:border-[#1A3C2B]"
                          style={{ borderColor: C.hairline, color: C.grid }}
                        >
                          {item.type === "Video" ? <ExternalLink className="w-4 h-4" /> : <Download className="w-4 h-4" />}
                        </span>
                      </div>
                      <h3 className="text-base md:text-lg font-bold leading-tight transition-colors group-hover:text-[#1A3C2B]" style={{ color: C.forest }}>
                        {item.title}
                      </h3>
                      <p className="mt-2 text-sm leading-relaxed" style={{ color: C.grid }}>
                        {item.desc}
                      </p>
                      <TechLabel className="mt-4 block" style={{ color: `${C.grid}99` }}>
                        item · 0{i + 1}
                      </TechLabel>
                    </button>
                  ))}
                </div>
              </motion.div>
            ))}
          </div>
        </PageContainer>
      </section>

      {/* BLOG TEASERS */}
      <section className="py-20 md:py-24 border-b" style={{ borderColor: C.hairline }}>
        <PageContainer>
          <div className="mb-12 flex items-end justify-between flex-wrap gap-4">
            <div className="max-w-2xl">
              <StatusBadge>From the blog</StatusBadge>
              <h2 className="mt-4 text-3xl md:text-4xl xl:text-5xl font-bold leading-[1.05] tracking-tight" style={{ color: C.forest }}>
                Recent reading.
              </h2>
            </div>
            <Link
              to={createPageUrl("Blog")}
              className="text-[10px] font-bold uppercase inline-flex items-center gap-1.5 transition-colors hover:text-[#1A3C2B]"
              style={{ letterSpacing: "0.18em", color: `${C.grid}cc` }}
            >
              All posts
              <ArrowRight className="w-3.5 h-3.5" />
            </Link>
          </div>

          <div className="grid md:grid-cols-3 gap-px" style={{ background: C.hairline }}>
            {teasers.map((t, i) => (
              <article key={t.title} className="p-7 md:p-8" style={{ background: C.paper }}>
                <h3 className="mt-2 text-lg font-bold leading-tight" style={{ color: C.forest }}>
                  {t.title}
                </h3>
                <p className="mt-2 text-sm leading-relaxed" style={{ color: C.grid }}>
                  {t.excerpt}
                </p>
                <div className="mt-5 pt-4 border-t flex items-center justify-between" style={{ borderColor: C.hairline }}>
                  <TechLabel style={{ color: `${C.grid}aa` }}>{t.date}</TechLabel>
                  <TechLabel style={{ color: `${C.grid}aa` }}>{t.readTime}</TechLabel>
                </div>
              </article>
            ))}
          </div>
        </PageContainer>
      </section>

      {/* CTA */}
      <section className="py-20 md:py-24">
        <PageContainer>
          <div className="relative border p-10 md:p-14 text-center" style={{ borderColor: C.hairline, background: C.paper }}>
            <CornerMarkers color={C.forest} />
            <div className="flex justify-center mb-5">
              <StatusBadge>Need something else?</StatusBadge>
            </div>
            <h2 className="text-3xl md:text-4xl xl:text-5xl font-bold leading-[1.05] tracking-tight" style={{ color: C.forest }}>
              Can't find a doc?
              <br />
              <span style={{ color: `${C.forest}80` }}>We'll write it.</span>
            </h2>
            <p className="mt-4 text-base md:text-lg max-w-2xl mx-auto" style={{ color: C.grid }}>
              If you need a custom report, ROI calc, or a security questionnaire drop us a note.
            </p>
            <div className="mt-8 flex flex-wrap items-center justify-center gap-3">
              <Link to={createPageUrl("Contact")}>
                <PrimaryButton>
                  <Sparkles className="w-4 h-4 mr-2" />
                  Request a doc
                </PrimaryButton>
              </Link>
              <Link to={createPageUrl("Blog")}>
                <button
                  type="button"
                  className="cursor-pointer h-12 px-6 text-sm font-semibold border bg-transparent inline-flex items-center hover:bg-[#1A3C2B]/[0.04] transition-colors duration-150"
                  style={{ borderColor: C.forest, color: C.forest }}
                >
                  <BookOpen className="w-4 h-4 mr-2" />
                  Read the blog
                </button>
              </Link>
            </div>
          </div>
        </PageContainer>
      </section>
    </div>
  );
}
